import * as ProgressPrimitive from "@radix-ui/react-progress"
import clsx from "clsx"

import { type SX, theme, type ThemeKeys } from "../../theme"
import { Text } from "../Text"
import styles from "./ProgressBar.module.css"

export interface ProgressBarProps {
  /**
   * The current value of the progress.
   */
  value: number
  /**
   * The maximum value of the progress.
   *
   * @defaultValue 100
   */
  max?: number
  /**
   * Value label is always attached to the element for the sake of
   * screen-readers.
   * If getValueLabel is not provided, the attached
   * label will be % value of progress.
   */
  getValueLabel?: (value: number, max: number) => string
  /**
   * Whether to display the value label on the page. Requires getValueLabel.
   *
   * @defaultValue false
   */
  displayLabel?: boolean
  /**
   * The color of the progress indicator.
   *
   * @defaultValue "green9"
   */
  color?: ThemeKeys<"color">
  /**
   * The color of the progress bar track.
   *
   * @defaultValue "grey4"
   */
  backgroundColor?: ThemeKeys<"color">
  /**
   * The color of the label.
   *
   * @defaultValue "grey11"
   */
  labelColor?: ThemeKeys<"color">
  /**
   * Whether to collapse the indicator bar. Used for idle and complete states.
   *
   * @defaultValue "normal"
   */
  height?: "collapsed" | "normal" | "large"
  /**
   * Slow transition applies 2s transition-duration - E.g.: uploading use-case.
   *
   * @defaultValue "fast"
   */
  transition?: "slow" | "fast"
  sx?: SX
}

export function ProgressBar(props: ProgressBarProps) {
  const {
    value,
    max = 100,
    getValueLabel,
    displayLabel = false,
    color = "green9",
    backgroundColor = "grey4",
    labelColor = "grey11",
    height = "normal",
    transition = "fast",
    sx
  } = props

  return (
    <div className={styles.root} style={sx}>
      <ProgressPrimitive.Root
        className={clsx(styles.progress, {
          [styles.collapsed]: height === "collapsed",
          [styles.large]: height === "large"
        })}
        style={{ backgroundColor: theme.color[backgroundColor] }}
        value={value}
        max={max}
        getValueLabel={getValueLabel}
      >
        <ProgressPrimitive.Indicator
          className={clsx(styles.indicator, {
            [styles.slowTransition]: transition === "slow"
          })}
          style={{
            transform: `translateX(-${100 - (value / max) * 100}%)`,
            backgroundColor:
              height !== "collapsed" ? theme.color[color] : undefined
          }}
        />
      </ProgressPrimitive.Root>
      {displayLabel && getValueLabel && (
        <Text variant="small" color={labelColor}>
          {getValueLabel(value, max)}
        </Text>
      )}
    </div>
  )
}
